import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { CONTACT } from "./nav";

const KEY = "jem-chat";
const GREETING = { role: "assistant", content: "Hi! I'm the JEM Guide. Ask me about the program, the GVRA pilot, who JEM serves, or how to partner with John." };
const SUGGESTIONS = ["What's included in the GVRA pilot?", "Who is JEM for?", "How do we book a screening?"];
const FALLBACK = `Sorry, I can't answer right now. Please email ${CONTACT.email} or call ${CONTACT.phone}.`;

const loadHistory = () => {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY));
    return Array.isArray(v) && v.length ? v : [GREETING];
  } catch {
    return [GREETING];
  }
};

const streamReply = async (messages, onDelta) => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages: messages.filter((m) => m !== GREETING && m.content).slice(-20) }),
  });
  if (!res.ok || !res.body) {
    const d = await res.json().catch(() => ({}));
    throw new Error(typeof d?.detail === "string" ? d.detail : FALLBACK);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const data = p.replace(/^data: /, "");
      if (data === "[DONE]") return;
      const j = JSON.parse(data);
      if (j.error) throw new Error(FALLBACK);
      onDelta(j.t ?? "");
    }
  }
};

const Bubble = ({ m, i }) => (
  <li className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${i}`}>
    <p className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[#F3C649] text-[#0A0B0E]" : "border border-[#D4AF37]/25 bg-[#181A24] text-[#F9F8F6]"}`}>
      {m.content || <Loader2 size={16} className="animate-spin text-[#F3C649]" aria-label="Typing" />}
    </p>
  </li>
);

const Panel = ({ onClose }) => {
  const [msgs, setMsgs] = useState(loadHistory);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(msgs.filter((m) => m.content))); }, [msgs]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [msgs]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const send = async (q) => {
    const content = q.trim();
    if (!content || busy) return;
    const next = [...msgs, { role: "user", content }];
    setMsgs([...next, { role: "assistant", content: "" }]);
    setText("");
    setBusy(true);
    const append = (t) => setMsgs((xs) => [...xs.slice(0, -1), { role: "assistant", content: xs[xs.length - 1].content + t }]);
    try {
      await streamReply(next, append);
    } catch (e) {
      setMsgs((xs) => [...xs.slice(0, -1), { role: "assistant", content: e.message || FALLBACK }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div role="dialog" aria-label="JEM Guide chat" data-lenis-prevent data-testid="chat-panel"
      initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="fixed bottom-24 right-4 z-[60] flex h-[min(560px,calc(100svh-8rem))] w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden rounded-3xl border border-[#D4AF37]/40 bg-[#0E1016] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] sm:right-6">
      <div className="flex items-center justify-between border-b border-[#D4AF37]/20 px-5 py-4">
        <div>
          <p className="font-display text-xl font-semibold">JEM Guide</p>
          <p className="text-xs text-[#C8C5BB]">AI assistant · answers may be imperfect</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close chat" className="rounded-full p-2 hover:bg-white/10" data-testid="chat-close-btn"><X size={18} aria-hidden="true" /></button>
      </div>
      <ul ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite" data-testid="chat-messages">
        {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        {msgs.length === 1 && (
          <li className="flex flex-wrap gap-2 pt-2">
            {SUGGESTIONS.map((s, i) => (
              <button key={s} type="button" onClick={() => send(s)} className="rounded-full border border-[#D4AF37]/40 px-3 py-1.5 text-xs text-[#F9F8F6] hover:bg-[#F3C649]/10" data-testid={`chat-suggestion-${i}`}>{s}</button>
            ))}
          </li>
        )}
      </ul>
      <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex items-center gap-2 border-t border-[#D4AF37]/20 p-3">
        <label htmlFor="chat-input" className="sr-only">Ask a question about JEM</label>
        <input id="chat-input" ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} maxLength={800} placeholder="Ask about JEM…" autoComplete="off"
          className="min-w-0 flex-1 rounded-full border border-[#D4AF37]/35 bg-[#12141C] px-4 py-3 text-sm text-[#F9F8F6] placeholder:text-[#A9A598]" data-testid="chat-input" />
        <button type="submit" disabled={busy || !text.trim()} aria-label="Send message" className="rounded-full bg-[#F3C649] p-3 text-[#0A0B0E] transition-opacity disabled:opacity-50" data-testid="chat-send-btn">
          {busy ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Send size={18} aria-hidden="true" />}
        </button>
      </form>
      <p className="px-4 pb-3 text-[0.7rem] text-[#A9A598]">Please don't share medical or personal health details.</p>
    </motion.div>
  );
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  if (!process.env.REACT_APP_BACKEND_URL) return null;
  return (
    <>
      <AnimatePresence>{open && <Panel onClose={() => setOpen(false)} />}</AnimatePresence>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={open ? "Close JEM Guide chat" : "Open JEM Guide chat"}
        className="fixed bottom-5 right-4 z-[60] flex items-center gap-2 rounded-full bg-gradient-to-br from-[#F3C649] to-[#B8932A] px-5 py-4 font-semibold text-[#0A0B0E] shadow-[0_15px_40px_-10px_rgba(243,198,73,0.6)] transition-transform duration-300 hover:-translate-y-0.5 sm:right-6"
        data-testid="chat-toggle-btn">
        {open ? <X size={20} aria-hidden="true" /> : <MessageCircle size={20} aria-hidden="true" />}
        <span className="hidden text-sm sm:inline">{open ? "Close" : "Ask JEM"}</span>
      </button>
    </>
  );
};
